/** @format */

import { Injectable } from '@angular/core';
import { MyAccountError } from './myaccount-error.interface';
import { MyAccountErrorLogKeys } from '@shared/data-access/enums';
import { AnalyticsService } from '../analytics.service';

/** @dynamic */
@Injectable({
	providedIn: 'root'
})
export class MyAccountAnalyticsService extends AnalyticsService {
	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setOrderId
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param orderId - The order id
	 *
	 * @description
	 * Sets the Order ID on the Object. Order ID is the order number represented on the order.
	 */
	public setOrderId(orderId: string): void {
		this.window.siteData.OrderID = orderId;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setOrderLevelPromoAmounts
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param promoAmounts - The array of promo amounts to set
	 *
	 * @description
	 * Sets an array of promo amounts on the Object.
	 */
	public setOrderLevelPromoAmounts(promoAmounts: object): void {
		this.window.siteData.OrderLevelPromoAmounts = promoAmounts;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setProductArray
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param productArray - The array of products to set
	 *
	 * @description
	 * Sets an array of products on the Object.
	 */
	public setProductArray(productArray: object): void {
		this.window.siteData.ProductArray = productArray;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setShippingMethod
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param shippingMethod - The array of shipping methods to set
	 *
	 * @description
	 * Sets an array of shipping methods on the Object.
	 */
	public setShippingMethod(shippingMethod: object): void {
		this.window.siteData.ShippingMethod = shippingMethod;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setTaxTotal
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param taxTotal - The tax total
	 *
	 * @description
	 * Sets the Tax Total on the Object.
	 */
	public setTaxTotal(taxTotal: string): void {
		this.window.siteData.TaxTotal = taxTotal;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setTotalOrderDiscount
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param totalOrderDiscount - The tax total
	 *
	 * @description
	 * Sets the Tax Total on the Object.
	 */
	public setTotalOrderDiscount(totalOrderDiscount: string): void {
		this.window.siteData.TotalOrderDiscount = totalOrderDiscount;
	}

	public setErrorAnalytics(message: string): void {
		this.setEventType('SiteError');
		this.setErrorId(message);
	}

	public generateLogError(
		errorKey: MyAccountErrorLogKeys | string,
		errorMessage: string,
		errorObject: any = undefined,
		additionalData: Partial<MyAccountError> = undefined
	) {
		const error: MyAccountError = {
			Error: errorObject,
			ErrorMessage: errorMessage,
			PageType: this.window.siteData?.PageType,
			Chain: this.window.siteData?.Chain,
			AppId: this.window.siteData?.appID,
			Authenticated: this.window.siteData?.LoggedIn,
			ReferringPageURL: `${this.document?.referrer}`,
			X_HREF: `${this.window.location?.protocol}//${this.window.location?.host}${this.window.location?.pathname}`, // TODO: Use HttpService instead, but will require a lot of changes to spec files
			DSG_UID: this.cookieService.get('DSG_UID')
		};

		this.setErrorAnalytics(errorMessage);

		super.generateLogError(errorKey, errorMessage, error, additionalData);
	}

	/**
	 * @description
	 * Posts to rumrunner when a missing product image is detected
	 */
	public emitMissingImageEvent(productInfo: {
		color?: string;
		ecode?: string;
		pageUrl: string;
		productName?: string;
		requestedImg: string;
	}): void {
		if (window['elkRUM'] && window['elkRUM'].setCustom) {
			window['elkRUM'].setCustom('missing_image', productInfo);
		}
	}
}
